import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
	deleteInvoicePaymentLock,
	getInvoicePaymentsLock,
} from '@/services/invoicePaymentLock';
import { createStripeCheckoutSession } from '@/services/stripe';
import type { FilterType } from '@/types/table';

export const useGetInvoicePaymentsLock = ({
	page = 1,
	limit = 15,
	enabled = true,
	columnsFilters = '[]',
	search = '',
	status = 'session',
	sort = '',
	dateRangeFilter = '',
}: FilterType) => {
	return useQuery({
		queryKey: [
			'Payment',
			page,
			limit,
			status,
			columnsFilters,
			search,
			sort,
			dateRangeFilter,
		],
		queryFn: () =>
			getInvoicePaymentsLock(
				page,
				limit,
				columnsFilters,
				search,
				status as string,
				sort as string,
			),
		enabled, // Replace 'true' with the appropriate condition if needed
		keepPreviousData: false, // Keep showing previous data while fetching new data
		// staleTime: 30000, // Consider data fresh for 30 seconds
	});
};

export const useGetStripeCheckoutSession = (
	onSuccess?: (data: {
		message: string;
		url: string | null;
		stripeSessionType: 'session' | 'link';
	}) => void,
	onError?: (errorMessage: string) => void,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: {
			invoiceRequestData: {
				clinicQBOId: string;
				invoiceNumber: string;
				clinicGUID: string;
				clinicState: string;
				bypassSurcharges: boolean;
			}[];
			userData: {
				email: string;
				name: string;
			};
			stripeSessionType: 'session' | 'link';
			paymentMethod: 'CREDIT_CARD' | 'BANK_ACCOUNT';
		}) => createStripeCheckoutSession(data),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ['StripeCheckoutSession'] });
			onSuccess?.(data);
		},
		onError: (error: {
			response?: {
				data?: {
					error?: {
						message?: string | { message?: string };
					};
				};
			};
		}) => {
			const errorMessage =
				typeof error?.response?.data?.error?.message === 'object'
					? error?.response?.data?.error?.message?.message ||
						'Failed to create Stripe Checkout Session. Please try again.'
					: error?.response?.data?.error?.message ||
						'Failed to create Stripe Checkout Session. Please try again.';
			onError?.(errorMessage);
		},
	});
};

export const useDeleteInvoicePaymentLock = (
	onSuccess?: () => void,
	onError?: (errorMessage: string) => void,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: { id: string[] }) => deleteInvoicePaymentLock(data.id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['Payment'] });
			onSuccess?.();
		},
		onError: (error: {
			response?: {
				data?: {
					error?: {
						message?: string | { message?: string };
					};
				};
			};
		}) => {
			const errorMessage =
				typeof error?.response?.data?.error?.message === 'object'
					? error?.response?.data?.error?.message?.message ||
						'Failed to delete invoice payment lock. Please try again.'
					: error?.response?.data?.error?.message ||
						'Failed to delete invoice payment lock. Please try again.';
			onError?.(errorMessage);
		},
	});
};
